import React, { useMemo, useState } from 'react';
import {
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import type { CountryCode } from 'libphonenumber-js';
import {
  formatPhoneInput,
  isPhoneOptionalValid,
  parseValidPhone,
  phoneCountryFromLocale,
} from '../lib/phone';
import { useI18n } from '../i18n/I18nProvider';

export type PhoneInputFieldProps = {
  value: string;
  onChange: (value: string) => void;
  /** Fires with E.164 when valid, else `null` (including empty). */
  onE164Change?: (e164: string | null) => void;
  label?: string;
  placeholder?: string;
  /** Overrides locale-based default (e.g. `RO`). */
  defaultCountry?: CountryCode;
  /** Force showing the invalid state (e.g. after submit). */
  showError?: boolean;
  disabled?: boolean;
  className?: string;
  /** Optional clear button in the trailing slot. */
  clearable?: boolean;
  onClear?: () => void;
};

/**
 * Ionic-native phone field with international validation (libphonenumber).
 * Use anywhere you collect a phone — Chats, Contacts, etc.
 */
const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  value,
  onChange,
  onE164Change,
  label,
  placeholder,
  defaultCountry,
  showError = false,
  disabled = false,
  className = 'vault-field-item',
  clearable = false,
  onClear,
}) => {
  const { t, locale } = useI18n();
  const [touched, setTouched] = useState(false);
  const country = defaultCountry ?? phoneCountryFromLocale(locale);

  const invalid = useMemo(
    () => Boolean(value.trim()) && !isPhoneOptionalValid(value, country),
    [value, country]
  );
  const revealError = (touched || showError) && invalid;

  const emit = (next: string) => {
    onChange(next);
    onE164Change?.(parseValidPhone(next, country));
  };

  return (
    <>
      <IonItem
        lines="none"
        className={`${className}${revealError ? ' phone-input-field--invalid' : ''}`}
      >
        {label ? <IonLabel position="stacked">{label}</IonLabel> : null}
        <IonInput
          type="tel"
          inputMode="tel"
          autocomplete="tel"
          enterkeyhint="done"
          value={value}
          disabled={disabled}
          placeholder={placeholder ?? t('phone.placeholder')}
          onIonInput={(e) => {
            emit(formatPhoneInput(e.detail.value ?? '', country));
          }}
          onIonBlur={() => {
            setTouched(true);
            const e164 = parseValidPhone(value, country);
            if (e164) emit(e164);
            else onE164Change?.(null);
          }}
        />
        {clearable && value ? (
          <IonButton
            slot="end"
            fill="clear"
            size="small"
            disabled={disabled}
            aria-label={t('common.actions.close')}
            onClick={() => {
              onClear?.();
              emit('');
              setTouched(false);
            }}
          >
            <IonIcon slot="icon-only" icon={closeOutline} />
          </IonButton>
        ) : null}
      </IonItem>
      {revealError ? (
        <IonText color="danger">
          <p className="phone-input-field__error">{t('phone.invalid')}</p>
        </IonText>
      ) : null}
      <style>{`
        .phone-input-field__error {
          margin: 0.15rem 0 0.35rem;
          padding: 0 0.75rem;
          font-size: 0.8rem;
          line-height: 1.3;
        }
        .phone-input-field--invalid {
          --border-color: var(--ion-color-danger);
        }
      `}</style>
    </>
  );
};

export default PhoneInputField;
